import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

/**
 * MEET MELINDA — vertical editorial flow (content driven heights, natural
 * image aspect ratios, no forced 100vh, no horizontal pinned scroll).
 *   1. Meet Melinda  (text + large full width five look collage)
 *   2. Her story     (text + yellow dress portrait, natural ratio)
 *   3. Everyday magic (flower artwork + quote, natural ratio)
 */
const PRESS = ['WGSN', 'ELLE', 'InStyle', 'Glamour', 'Marie Claire', 'Frankie'];

const PressLogos = ({ className = '' }) => (
  <div className={`flex flex-wrap items-baseline justify-center gap-x-7 gap-y-2 ${className}`}>
    {PRESS.map((logo) => (
      <span
        key={logo}
        className={`text-neutral-900 ${
          logo === 'ELLE'
            ? 'text-2xl lg:text-3xl italic'
            : logo === 'Glamour'
            ? 'text-lg lg:text-xl font-bold uppercase'
            : logo === 'Frankie'
            ? 'text-lg lg:text-xl italic'
            : 'text-base lg:text-lg font-bold'
        }`}
        style={{
          fontFamily:
            logo === 'ELLE' || logo === 'Frankie' ? "'Coolvetica', 'Inter', sans-serif" : undefined,
          letterSpacing: logo === 'Glamour' ? '0.12em' : undefined,
        }}
      >
        {logo}
      </span>
    ))}
  </div>
);

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8 },
};

const IntroScrollSection = () => {
  const { t } = useLanguage();
  const paragraphs = t('introStoryParagraphs') || [];

  return (
    <div id="meet-melinda" className="bg-[#faf7ee]" data-testid="intro-scroll-section">
      {/* 1 — MEET MELINDA */}
      <section className="container mx-auto px-6 lg:px-8 pt-24 lg:pt-28 pb-16 lg:pb-20" data-testid="intro-eye-slide">
        <motion.div {...fade} className="max-w-4xl mx-auto text-center space-y-4 lg:space-y-5">
          <h2
            className="leading-[0.92] text-neutral-900"
            style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2.5rem, 6vw, 4.75rem)' }}
            data-testid="intro-eye-title"
          >
            {t('introEyeTitle')}
          </h2>
          <p className="text-lg lg:text-2xl italic text-neutral-700">{t('introEyeSubtitle')}</p>
          <div className="mx-auto w-14 h-[3px] bg-[#CCFF00]" />
          <p
            className="text-neutral-900 leading-[1.15] max-w-3xl mx-auto"
            style={{ fontWeight: 700, fontSize: 'clamp(1.35rem, 2.4vw, 2.1rem)' }}
            data-testid="intro-eye-statement"
          >
            {t('introEyeStatement')}
          </p>
          <p className="text-[11px] lg:text-xs tracking-[0.4em] uppercase text-neutral-600 max-w-2xl mx-auto pt-1">
            {t('introEyeCred')}
          </p>
          <PressLogos className="pt-1" />
        </motion.div>

        {/* five look collage — large, full content width, natural aspect */}
        <motion.div {...fade} className="mt-10 lg:mt-14">
          <img
            src="/outfit-strip.jpg"
            alt={t('introEyeTitle')}
            className="block w-full h-auto"
            loading="lazy"
            decoding="async"
            data-testid="intro-outfit-strip"
          />
        </motion.div>
      </section>

      {/* 2 — HER STORY */}
      <section className="container mx-auto px-6 lg:px-8 py-16 lg:py-20" data-testid="intro-story-slide">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div {...fade} className="order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs lg:text-sm tracking-[0.4em] font-bold uppercase" style={{ color: '#7d65b8' }}>
                {t('introStoryEyebrow')}
              </span>
              <span className="h-px flex-1 bg-neutral-300" />
            </div>
            <div
              className="text-neutral-800 sm:columns-2 sm:gap-10 [column-fill:_balance]"
              style={{ fontSize: 'clamp(14px, 1vw, 16px)', lineHeight: 1.6 }}
              data-testid="intro-story-body"
            >
              {paragraphs.map((p, i) => (
                <p
                  key={i}
                  className={`mb-3 ${i === 0 ? 'first-letter:text-[2.8em] first-letter:leading-[0.8] first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:font-bold first-letter:text-neutral-900' : ''}`}
                  style={{ breakInside: 'avoid' }}
                >
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-neutral-300 flex items-baseline gap-3 flex-wrap">
              <span className="w-10 h-[3px] bg-[#CCFF00]" />
              <span className="text-neutral-900" style={{ fontWeight: 700, fontSize: 'clamp(1.2rem, 1.7vw, 1.6rem)' }} data-testid="intro-story-signature">
                {t('introStorySignature')}
              </span>
              <span className="text-xs lg:text-sm uppercase tracking-[0.3em]" style={{ color: '#7d65b8' }}>
                {t('introStoryLocation')}
              </span>
            </div>
          </motion.div>

          <motion.div {...fade} className="order-1 lg:order-2">
            <img
              src="/melinda-photo.jpg"
              alt="Melinda Tóth"
              className="block w-full h-auto"
              loading="lazy"
              data-testid="intro-yellow-dress"
            />
          </motion.div>
        </div>
      </section>

      {/* 3 — EVERYDAY MAGIC */}
      <section className="container mx-auto px-6 lg:px-8 py-16 lg:py-20" data-testid="intro-quote-slide">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div {...fade} className="relative">
            <img
              src="/melinda-flower.jpg"
              alt={t('introQuoteCaption')}
              className="block w-full h-auto"
              loading="lazy"
              data-testid="intro-flower"
            />
            <span className="absolute bottom-4 left-4 text-[10px] tracking-[0.35em] uppercase text-white/85">
              {t('introQuoteCaption')}
            </span>
          </motion.div>

          <motion.div {...fade} className="space-y-6">
            <span className="block text-[11px] lg:text-xs tracking-[0.45em] font-bold uppercase" style={{ color: '#7d65b8' }}>
              {t('introQuoteEyebrow')}
            </span>
            <blockquote className="text-neutral-900 italic leading-[1.3]" style={{ fontSize: 'clamp(1.35rem, 2.2vw, 2.05rem)', fontWeight: 400 }} data-testid="intro-quote-text">
              “{t('introQuoteText')}”
            </blockquote>
            <div className="flex items-center gap-4">
              <span className="w-12 h-[3px] bg-[#CCFF00]" />
              <span className="text-neutral-900" style={{ fontWeight: 700, fontSize: 'clamp(1.1rem, 1.5vw, 1.4rem)' }} data-testid="intro-quote-author">
                {t('introQuoteAuthor')}
              </span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IntroScrollSection;
